import { FieldErrors, UseFormClearErrors, UseFormRegister } from "react-hook-form";
import {
  ChangeShippingOptions,
  formControlRegionName,
  formControlShippingOptionName,
  regionName
} from "../../shared/models/customerShippingOptions";
import colors from "../../../shared/themes/colors";
import { Box, Checkbox, InputLabel, TextField } from "@mui/material";
import React, { useEffect } from "react";
import { ShippingOptionCode, ShippingRegionCode } from "../../../shared/models/models";
import { CurrencyCode } from "../../../common/models/CommonModels";
import useTranslation from "../../../shared/hooks/useTranslation";

interface ShippingOptionProps {
  countryId: number;
  register: UseFormRegister<ChangeShippingOptions>;
  clearErrors: UseFormClearErrors<ChangeShippingOptions>
  errors: FieldErrors<ChangeShippingOptions>;
  isActive: boolean;
  regionCode: ShippingRegionCode;
  typeCode: ShippingOptionCode;
  minPeriod: number;
}

const ShippingOptionStandardOrExpress = ({
  countryId,
  register,
  clearErrors,
  isActive,
  regionCode,
  typeCode,
  minPeriod,
  errors
}: ShippingOptionProps) => {
  const { translate } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");
  useEffect(() => {
    clearErrors();
  }, [minPeriod]);

  useEffect(() => {
    clearErrors();
  }, [isActive]);

  return (
    <Box
      className="flex items-start py-2 px-4"
      sx={{
        minHeight: "128px",
        background: isActive ? colors.secondary.gray96 : "",
        borderRadius: "8px"
      }}
    >
      <Checkbox
        {...register(`${formControlShippingOptionName(typeCode)}.${formControlRegionName(regionCode)}.isActive`)}
        checked={isActive}
      />

      <Box className="flex w-full gap-4" sx={{ marginTop: "9px" }}>
        <Box className="text-16-bold" color="#333333" sx={{ width: "250px" }}>
          {regionName(regionCode, countryId)}
        </Box>

        <Box className="flex justify-between w-full">
          <Box className="mt-1">
            <Box className="text-14-bold">{translate("estimatedDeliveryTime")}</Box>
            <Box className="flex gap-2 mt-2">
              <Box>
                <InputLabel required>{translate("minPeriod")}</InputLabel>
                <TextField
                  type="number"
                  variant="outlined"
                  placeholder="0"
                  sx={{
                    width: "125px",
                    ".MuiOutlinedInput-root": {
                      borderRadius: "4px",
                      background: "#fff"
                    }
                  }}
                  {...register(
                    `${formControlShippingOptionName(typeCode)}.${formControlRegionName(regionCode)}.fromBusinessDays`,
                    {
                      required: isActive ? tCommon("forms.required") : "",
                      max: { value: 365, message: translate("maxValue365") },
                      min: { value: 0, message: translate("minValue0") }
                    }
                  )}
                  error={
                    !!errors[formControlShippingOptionName(typeCode)]?.[formControlRegionName(regionCode)]
                      ?.fromBusinessDays
                  }
                  helperText={
                    errors[formControlShippingOptionName(typeCode)]?.[formControlRegionName(regionCode)]
                      ?.fromBusinessDays?.message
                  }
                />
              </Box>

              <Box sx={{ marginTop: "30px" }}>—</Box>

              <Box>
                <InputLabel required>{translate("maxPeriod")}</InputLabel>
                <TextField
                  type="number"
                  variant="outlined"
                  placeholder="0"
                  sx={{
                    width: "125px",
                    ".MuiOutlinedInput-root": {
                      borderRadius: "4px",
                      background: "#fff"
                    }
                  }}
                  {...register(
                    `${formControlShippingOptionName(typeCode)}.${formControlRegionName(regionCode)}.toBusinessDays`,
                    {
                      required: isActive ? tCommon("forms.required") : "",
                      max: { value: 365, message: translate("maxValue365") },
                      min: { value: 0, message: translate("minValue0") },
                      valueAsNumber: true,
                      validate: (value) => {
                        if (isActive) {
                          return value >= minPeriod || translate("mustBeGreaterThan", { minPeriod });
                        }
                      }
                    }
                  )}
                  error={
                    !!errors[formControlShippingOptionName(typeCode)]?.[formControlRegionName(regionCode)]
                      ?.toBusinessDays
                  }
                  helperText={
                    errors[formControlShippingOptionName(typeCode)]?.[formControlRegionName(regionCode)]?.toBusinessDays
                      ?.message
                  }
                />
              </Box>
            </Box>
          </Box>
          <Box className="mt-1">
            <Box className="text-14-bold">{translate("shippingPrice")}</Box>
            <Box className="flex gap-4 mt-2">
              <Box>
                <InputLabel required>{translate("costEuro")}</InputLabel>
                <TextField
                  type="number"
                  variant="outlined"
                  placeholder="200"
                  sx={{
                    width: "125px",
                    ".MuiOutlinedInput-root": {
                      borderRadius: "4px",
                      background: "#fff"
                    }
                  }}
                  {...register(
                    `${formControlShippingOptionName(typeCode)}.${formControlRegionName(regionCode)}.orderPrice.value`,
                    {
                      required: isActive ? tCommon("forms.required") : "",
                      max: { value: 10000, message: translate("maxValue10000") },
                      min: { value: 0, message: translate("minValue0") },
                      valueAsNumber: true,
                    }
                  )}
                  error={
                    !!errors[formControlShippingOptionName(typeCode)]?.[formControlRegionName(regionCode)]?.orderPrice
                      ?.value
                  }
                  helperText={
                    errors[formControlShippingOptionName(typeCode)]?.[formControlRegionName(regionCode)]?.orderPrice
                      ?.value?.message
                  }
                />
                <input
                  type="hidden"
                  {...register(
                    `${formControlShippingOptionName(typeCode)}.${formControlRegionName(regionCode)}.orderPrice.id`,
                    { value: CurrencyCode.eur }
                  )}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ShippingOptionStandardOrExpress;